/**
 * The one layout every email in this system uses.
 *
 * §11.6 fixes the shape for the kitchen documents — "table-based HTML email
 * (Raleway with Arial fallback, 680 px)" — and §19.5 asks for the whole set
 * "on one shared layout", so customer, finance and admin mail use the same
 * one. Tables and inline styles rather than CSS, because that is what mail
 * clients render: Outlook's engine ignores a `<style>` block in the head,
 * `flex` and `grid` entirely, and anything positioned.
 *
 * Raleway is a web font. A mail client that will not load it falls back to
 * Arial, which is the §11.6 instruction and also what the Drive documents do
 * (§14 copy conventions). Nothing in the layout depends on which one renders.
 *
 * Every string that reaches the HTML goes through `escape()`. A dish name, a
 * customer's own note and a staff reason are all text, and `build/FIXES.md`
 * item 28 — escape every interpolation of customer input — is as true in an
 * email as it is on the page.
 */

import { brandTagline } from './brand.ts'

export const WIDTH_PX = 680

/** Run 12 item 3c (DECIDED 20261011): no email is wider than 600 px. The
 *  kitchen's documents keep the 680 px §11.6 fixes for them; every other
 *  message — customer, catering, finance, admin — is 600. */
export const CUSTOMER_WIDTH_PX = 600

export const FONT_STACK = "Raleway, Arial, 'Helvetica Neue', Helvetica, sans-serif"

/** Light theme only (§5.12). One ink, one paper, one green, one muted grey. */
export const COLOURS = {
  ink: '#1d2b22',
  muted: '#5d6b63',
  paper: '#ffffff',
  page: '#f4f1ea',
  line: '#d8d4ca',
  green: '#2f6b4f',
  greenSoft: '#eaf1ec',
  warn: '#8a5a12',
  warnSoft: '#fdf3e2',
  stop: '#8a2b2b',
  stopSoft: '#fbecec',
} as const

export function escape(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export type Tone = 'neutral' | 'good' | 'warn' | 'stop'

export type Cell =
  | string
  | {
      text: string
      align?: 'left' | 'right'
      strong?: boolean
      muted?: boolean
      indent?: boolean
      /** Run 10 item 7b: the first `italic` characters of `text` are set in
       *  italics — a plant-based look-alike dish name. */
      italic?: number
      /** Run 12 item F: the [start, end) ranges of `text` set in italics — a
       *  marked expression, whose asterisk is already in the text after it. */
      italics?: ReadonlyArray<readonly [number, number]>
    }

export type Block =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'facts'; rows: [string, string][] }
  | { kind: 'table'; head: Cell[]; rows: Cell[][] }
  | { kind: 'band'; text: string; tone: Tone }
  | { kind: 'button'; label: string; href: string }
  /** Run 12 item G: a plain link under a button, for the console's home page. */
  | { kind: 'link'; label: string; href: string }
  | { kind: 'note'; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'rule' }

export interface Email {
  subject: string
  /** The one line a mail client shows beside the subject in the inbox list. */
  preheader: string
  blocks: Block[]
}

export interface RenderOptions {
  /** Named in the footer so a recipient can tell a test send from a real one.
   *  `log` while the log driver is the one sending. The stage never invents a
   *  sender address to put here. */
  driver?: string
  /** §10 Sender: `Reply-To: catering@planteen.hu`. In the footer so a
   *  customer always has somewhere to write — §11.8 asks them to reply. */
  replyTo?: string | null
  siteUrl?: string | null
  /** A band above everything, for the preview page: an email rendered from
   *  the sample order is not an email about a real order. */
  banner?: string | null
  /** Run 9, item 1: the wordmark and footer name the company as the language
   *  does -- *Planteen* in Hungarian, *The Planteen* in English. */
  lang?: string
  /** Run 12 item 3c: the table width; `WIDTH_PX` when absent. */
  width?: number
}

interface NormalCell {
  text: string
  align: 'left' | 'right'
  strong: boolean
  muted: boolean
  indent: boolean
  italic: number
  italics: ReadonlyArray<readonly [number, number]>
}

const cell = (value: Cell): NormalCell =>
  typeof value === 'string'
    ? { text: value, align: 'left', strong: false, muted: false, indent: false, italic: 0, italics: [] }
    : {
        text: value.text,
        align: value.align ?? 'left',
        strong: value.strong ?? false,
        muted: value.muted ?? false,
        indent: value.indent ?? false,
        italic: value.italic ?? 0,
        italics: value.italics ?? [],
      }

/** A cell's text, with its leading italic part where it has one. */
const cellHtml = (c: NormalCell): string => {
  if (c.italics.length > 0) {
    let out = ''
    let at = 0
    for (const [start, end] of [...c.italics].sort((a, b) => a[0] - b[0])) {
      if (start < at || end > c.text.length) continue
      out += escape(c.text.slice(at, start)) + `<em style="font-style:italic;">${escape(c.text.slice(start, end))}</em>`
      at = end
    }
    return out + escape(c.text.slice(at))
  }
  return c.italic > 0
    ? `<em style="font-style:italic;">${escape(c.text.slice(0, c.italic))}</em>${escape(c.text.slice(c.italic))}`
    : escape(c.text)
}

function renderBlock(block: Block): string {
  switch (block.kind) {
    case 'heading':
      return (
        `<tr><td style="padding:22px 28px 6px 28px;font:600 17px/1.35 ${FONT_STACK};` +
        `color:${COLOURS.ink};">${escape(block.text)}</td></tr>`
      )

    case 'paragraph':
      return (
        `<tr><td style="padding:8px 28px;font:400 15px/1.55 ${FONT_STACK};` +
        `color:${COLOURS.ink};">${escape(block.text)}</td></tr>`
      )

    case 'note':
      return (
        `<tr><td style="padding:8px 28px;font:400 13px/1.5 ${FONT_STACK};` +
        `color:${COLOURS.muted};">${escape(block.text)}</td></tr>`
      )

    case 'rule':
      return (
        `<tr><td style="padding:10px 28px;"><div style="height:1px;background:${COLOURS.line};` +
        `line-height:1px;font-size:0;">&nbsp;</div></td></tr>`
      )

    case 'list':
      return (
        `<tr><td style="padding:4px 28px 8px 28px;">` +
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">` +
        block.items
          .map(
            (item) =>
              `<tr><td width="14" valign="top" style="padding:3px 0;font:400 15px/1.55 ${FONT_STACK};` +
              `color:${COLOURS.green};">&bull;</td>` +
              `<td valign="top" style="padding:3px 0;font:400 15px/1.55 ${FONT_STACK};` +
              `color:${COLOURS.ink};">${escape(item)}</td></tr>`,
          )
          .join('') +
        `</table></td></tr>`
      )

    case 'band': {
      const tones = {
        neutral: [COLOURS.page, COLOURS.ink],
        good: [COLOURS.greenSoft, COLOURS.green],
        warn: [COLOURS.warnSoft, COLOURS.warn],
        stop: [COLOURS.stopSoft, COLOURS.stop],
      } as const
      const [background, colour] = tones[block.tone]
      return (
        `<tr><td style="padding:10px 28px;">` +
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" ` +
        `style="background:${background};border-left:4px solid ${colour};">` +
        `<tr><td style="padding:12px 16px;font:600 14px/1.5 ${FONT_STACK};` +
        `color:${colour};">${escape(block.text)}</td></tr>` +
        `</table></td></tr>`
      )
    }

    case 'button':
      // A table, not an anchor with padding: Outlook collapses the padding.
      return (
        `<tr><td style="padding:16px 28px;">` +
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>` +
        `<td style="background:${COLOURS.green};border-radius:4px;">` +
        `<a href="${escape(block.href)}" style="display:inline-block;padding:13px 24px;` +
        `font:600 15px/1 ${FONT_STACK};color:#ffffff;text-decoration:none;">${escape(block.label)}</a>` +
        `</td></tr></table>` +
        `<div style="padding-top:8px;font:400 12px/1.5 ${FONT_STACK};color:${COLOURS.muted};` +
        `word-break:break-all;">${escape(block.href)}</div>` +
        `</td></tr>`
      )

    case 'link':
      return (
        `<tr><td style="padding:0 28px 12px;font:400 13px/1.5 ${FONT_STACK};color:${COLOURS.muted};">` +
        `<a href="${escape(block.href)}" style="color:${COLOURS.green};">${escape(block.label)}</a>` +
        ` · <span style="word-break:break-all;">${escape(block.href)}</span>` +
        `</td></tr>`
      )

    case 'facts':
      return (
        `<tr><td style="padding:6px 28px;">` +
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">` +
        block.rows
          .map(
            ([label, value]) =>
              `<tr><td width="38%" valign="top" style="padding:5px 12px 5px 0;` +
              `font:400 14px/1.5 ${FONT_STACK};color:${COLOURS.muted};">${escape(label)}</td>` +
              `<td valign="top" style="padding:5px 0;font:500 14px/1.5 ${FONT_STACK};` +
              `color:${COLOURS.ink};">${escape(value)}</td></tr>`,
          )
          .join('') +
        `</table></td></tr>`
      )

    case 'table':
      return (
        `<tr><td style="padding:10px 28px;">` +
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" ` +
        `style="border-collapse:collapse;">` +
        (block.head.length > 0
          ? `<tr>${block.head
              .map((head) => {
                const c = cell(head)
                return (
                  `<th align="${c.align}" style="padding:6px 8px;border-bottom:1px solid ${COLOURS.line};` +
                  `font:600 13px/1.4 ${FONT_STACK};color:${COLOURS.muted};` +
                  `text-align:${c.align};">${escape(c.text)}</th>`
                )
              })
              .join('')}</tr>`
          : '') +
        block.rows
          .map(
            (row) =>
              `<tr>${row
                .map((value) => {
                  const c = cell(value)
                  const padding = c.indent ? '6px 8px 6px 24px' : '6px 8px'
                  return (
                    `<td align="${c.align}" style="padding:${padding};` +
                    `border-bottom:1px solid ${COLOURS.line};` +
                    `font:${c.strong ? 600 : 400} ${c.muted ? 13 : 14}px/1.45 ${FONT_STACK};` +
                    `color:${c.muted ? COLOURS.muted : COLOURS.ink};` +
                    `text-align:${c.align};">${cellHtml(c)}</td>`
                  )
                })
                .join('')}</tr>`,
          )
          .join('') +
        `</table></td></tr>`
      )
  }
}

/**
 * The whole message.
 *
 * The preheader is hidden text at the very top; without one, a mail client
 * shows the first words of the layout in the inbox list, which would be the
 * logotype on every message in the account.
 */
export function renderEmail(email: Email, options: RenderOptions = {}): string {
  const en = options.lang === 'en'
  const width = options.width ?? WIDTH_PX
  const footer = footerLines(options)

  return phoneClasses(
    `<!doctype html>\n<html lang="${en ? 'en' : 'hu'}"><head><meta charset="utf-8">` +
    `<meta name="viewport" content="width=device-width,initial-scale=1">` +
    `<meta name="color-scheme" content="light"><meta name="supported-color-schemes" content="light">` +
    `<title>${escape(email.subject)}</title>${PHONE_STYLE}</head>` +
    `<body style="margin:0;padding:0;background:${COLOURS.page};">` +
    `<div style="display:none;max-height:0;overflow:hidden;opacity:0;">${escape(email.preheader)}</div>` +
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" ` +
    `style="background:${COLOURS.page};">` +
    `<tr><td class="outer" align="center" style="padding:24px 12px;">` +
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="${width}" ` +
    `style="width:100%;max-width:${width}px;background:${COLOURS.paper};border:1px solid ${COLOURS.line};">` +
    (options.banner
      ? `<tr><td style="padding:12px 28px;background:${COLOURS.warnSoft};` +
        `font:600 13px/1.5 ${FONT_STACK};color:${COLOURS.warn};">${escape(options.banner)}</td></tr>`
      : '') +
    `<tr><td style="padding:20px 28px 4px 28px;font:700 18px/1.2 ${FONT_STACK};` +
    `color:${COLOURS.green};letter-spacing:0.02em;">${en ? 'The Planteen' : 'Planteen'}</td></tr>` +
    `<tr><td style="padding:0 28px;font:400 13px/1.4 ${FONT_STACK};color:${COLOURS.muted};">` +
    `${escape(tagline(options))}</td></tr>` +
    email.blocks.map(renderBlock).join('') +
    `<tr><td style="padding:18px 28px 24px 28px;border-top:1px solid ${COLOURS.line};` +
    `font:400 12px/1.6 ${FONT_STACK};color:${COLOURS.muted};">` +
    footer.map((line) => escape(line)).join('<br>') +
    (options.driver ? `<br>[${escape(options.driver)}]` : '') +
    `</td></tr>` +
    `</table></td></tr></table></body></html>`
  )
}

/**
 * Run 12 item 3c (a bug its screenshots found): at 375 px every email
 * scrolled sideways — the table kept its 28 px side padding and the outer
 * 12 px, which left 295 px for an Étel table whose amounts need 330. On a
 * phone (480 px and below) the padding narrows. The inline styles stay what
 * every client renders; a client that ignores `<style>`, as Outlook's does,
 * renders the desktop layout it rendered before.
 */
const PHONE_STYLE =
  '<style>@media only screen and (max-width:480px){' +
  'td.outer{padding:12px 0!important}' +
  'td.px{padding-left:12px!important;padding-right:12px!important}' +
  'td.cell,th.cell{padding-left:4px!important;padding-right:4px!important}' +
  '}</style>'

/** Marks the cells PHONE_STYLE narrows: rows padded 28 px at the sides, and
 *  table cells padded 8 px. */
const phoneClasses = (html: string): string =>
  html
    .replace(/<td style="padding:([^";]*28px)/g, '<td class="px" style="padding:$1')
    .replace(/<(td|th) align="(left|right)" style="padding:6px 8px/g, '<$1 class="cell" align="$2" style="padding:6px 8px')

/** The line under the wordmark. Run 12 item 8: in English for an English
 *  message (it was Hungarian in both). DECIDED 20261019, Barna: the words
 *  are `BRAND_TAGLINE`'s, shared with the page and the legal pages. */
const tagline = (options: RenderOptions): string => brandTagline(options.lang)

/** The footer lines, shared by the HTML and the plain-text part. Run 12 item
 *  8: the reply line in English for an English message. */
function footerLines(options: RenderOptions): string[] {
  const en = options.lang === 'en'
  const footer: string[] = []
  if (options.replyTo) {
    footer.push(
      en
        ? `Reply to this email, or write to: ${options.replyTo}`
        : `Válaszoljon erre a levélre, vagy írjon ide: ${options.replyTo}`,
    )
  }
  if (options.siteUrl) footer.push(options.siteUrl)
  /* DECIDED 20261019, Barna: the footer carries the one brand line, the
     same `BRAND_TAGLINE` the header above it and the page show, in place of
     the elliptical *Planteen · …* tag it used to write for itself. The name
     is still the first line of the email, where `renderText` and the HTML
     header put it — *Planteen* in Hungarian, *The Planteen* in English, as
     run 9 item 1 decided. */
  footer.push(tagline(options))
  return footer
}

const cellText = (value: Cell): string => (typeof value === 'string' ? value : value.text)

/**
 * Run 12 item 3c: the plain-text part, built from the same blocks as the
 * HTML, so the two cannot say different things. A mail client that shows
 * text only, and a spam filter that compares the two, both read this.
 */
export function renderText(email: Email, options: RenderOptions = {}): string {
  const out: string[] = []
  if (options.banner) out.push(options.banner, '')
  out.push(options.lang === 'en' ? 'The Planteen' : 'Planteen', tagline(options), '')
  for (const block of email.blocks) {
    switch (block.kind) {
      case 'heading':
        out.push('', block.text.toUpperCase(), '')
        break
      case 'paragraph':
      case 'note':
        out.push(block.text, '')
        break
      case 'band':
        out.push(`> ${block.text}`, '')
        break
      case 'rule':
        out.push('----------------------------------------', '')
        break
      case 'list':
        for (const item of block.items) out.push(`- ${item}`)
        out.push('')
        break
      case 'button':
      case 'link':
        out.push(`${block.label}: ${block.href}`, '')
        break
      case 'facts':
        for (const [label, value] of block.rows) out.push(`${label}: ${value}`)
        out.push('')
        break
      case 'table':
        if (block.head.length > 0) out.push(block.head.map(cellText).filter((text) => text !== '').join(' | '))
        for (const row of block.rows) out.push(row.map(cellText).filter((text) => text !== '').join(' | '))
        out.push('')
        break
    }
  }
  out.push('--', ...footerLines(options))
  if (options.driver) out.push(`[${options.driver}]`)
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n'
}
